import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const COOKIE_NAME = "fc_affiliate";
const COOKIE_DAYS = 30;
const CLIENT_ID_KEY = "fc_client_id";

// Stable per-browser fingerprint used as ip_hash surrogate (we can't see the
// real IP from client JS). Stored in localStorage so repeat visits from the
// same browser dedupe for 24 hours server-side.
function getOrCreateClientId(): string {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

async function hashClientFingerprint(): Promise<string> {
  const raw = `${getOrCreateClientId()}::${navigator.userAgent}`;
  try {
    const data = new TextEncoder().encode(raw);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return raw.slice(0, 64);
  }
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

export function getAffiliateCode(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Reads ?ref= or ?via= from URL, sets a 30-day cookie, and records a click.
 * Call once at the app root.
 */
export function useAffiliateTracking() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref") || params.get("via");
    if (!code) return;

    // Don't overwrite an existing cookie from the same affiliate
    const existing = getAffiliateCode();
    if (existing === code) return;

    setCookie(COOKIE_NAME, code, COOKIE_DAYS);

    // Record click with an ip_hash surrogate so the server-side RPC can
    // dedupe the same browser inside a 24h window (fraud prevention).
    hashClientFingerprint().then((ipHash) => {
      supabase.rpc("record_affiliate_click", {
        p_code: code,
        p_ip_hash: ipHash,
        p_user_agent: navigator.userAgent,
        p_landing_page: window.location.pathname,
      }).then(({ error }) => {
        if (error) console.warn("[affiliate] click tracking failed:", error.message);
      });
    });

    // Clean URL params without reload
    params.delete("ref");
    params.delete("via");
    const clean = params.toString();
    const newUrl = window.location.pathname + (clean ? `?${clean}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, []);
}
